import React, { useState, useEffect, useRef } from 'react';
import { GameState } from '../types/game';
import { Card } from './Card';
import { findRecipe } from '../logic/recipes';
import { AlchemyTreeModal } from './AlchemyTreeModal';
import './GameBoard.css';

interface GameBoardProps {
  gameState: GameState;
  playerId: 1 | 2;
  isSpectator?: boolean;
  onCombine: (cardId1: string, cardId2: string) => void;
  onDiscard: (cardIds: string[]) => void;
  onEndTurn: () => void;
  onSurrender: () => void;
  onBackToMenu: () => void;
  combiningCards?: string[] | null; // Карты, которые сейчас комбинируются (для анимации оппонента/бота)
}

export const GameBoard: React.FC<GameBoardProps> = ({
  gameState,
  playerId,
  isSpectator = false,
  onCombine,
  onDiscard,
  onEndTurn,
  onSurrender,
  onBackToMenu,
  combiningCards = null,
}) => {
  const [selectedCards, setSelectedCards] = useState<string[]>([]);
  const [mode, setMode] = useState<'combine' | 'discard'>('combine');
  const [animatingCard, setAnimatingCard] = useState<string | null>(null);
  const [isTreeModalOpen, setIsTreeModalOpen] = useState(false);
  const [timeLeft, setTimeLeft] = useState<number>(90);

  // Ref для onEndTurn чтобы таймер не перезапускался при каждой комбинации
  const onEndTurnRef = useRef(onEndTurn);

  const isMyTurn = gameState.currentTurn === playerId;
  const myState = playerId === 1 ? gameState.player1 : gameState.player2;
  const opponentState = playerId === 1 ? gameState.player2 : gameState.player1;
  const opponentId = playerId === 1 ? 2 : 1;

  // Обновляем ref при изменении onEndTurn
  useEffect(() => {
    onEndTurnRef.current = onEndTurn;
  }, [onEndTurn]);

  // Turn timer - 90 seconds per turn
  useEffect(() => {
    if (!isMyTurn) {
      setTimeLeft(90);
      return;
    }

    setTimeLeft(90);
    const interval = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          // Time's up - auto end turn
          clearInterval(interval);
          onEndTurnRef.current();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isMyTurn]);

  // Обработчик клавиши 'R' для переключения дерева алхимии
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'r' || e.key === 'R' || e.key === 'к' || e.key === 'К') {
        setIsTreeModalOpen(prev => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleCardClick = (cardId: string) => {
    if (!isMyTurn) return;

    // Игнорируем клики во время анимации
    if (animatingCard !== null) return;

    // Найдем карту
    const clickedCard = [...myState.hand, ...gameState.table].find(c => c.id === cardId);

    // Если карта не найдена в текущем состоянии, игнорируем клик
    if (!clickedCard) return;

    if (mode === 'combine') {
      // Режим комбинирования
      if (selectedCards.includes(cardId)) {
        // Снять выделение
        setSelectedCards(selectedCards.filter(id => id !== cardId));
      } else if (selectedCards.length === 0) {
        // Выбрать первую карту
        setSelectedCards([cardId]);
      } else if (selectedCards.length === 1) {
        // Выбрать вторую карту и автоматически комбинировать
        const firstCard = [...myState.hand, ...gameState.table].find(c => c.id === selectedCards[0]);
        const secondCard = [...myState.hand, ...gameState.table].find(c => c.id === cardId);

        if (firstCard && secondCard) {
          const recipe = findRecipe(firstCard.element, secondCard.element);
          if (recipe) {
            // Сразу выполняем комбинацию
            onCombine(selectedCards[0], cardId);
            setSelectedCards([]);

            // Анимация запускается и очищается автоматически через CSS
            setAnimatingCard(cardId);
            setTimeout(() => setAnimatingCard(null), 300);
          } else {
            // Нет рецепта - просто переключаем выбор на новую карту
            setSelectedCards([cardId]);
          }
        }
      }
    } else {
      // Режим сброса
      if (selectedCards.includes(cardId)) {
        setSelectedCards(selectedCards.filter(id => id !== cardId));
      } else if (selectedCards.length < 4) {
        setSelectedCards([...selectedCards, cardId]);
      }
    }
  };

  const handleDiscard = () => {
    // If no cards selected, cancel discard mode (misclick protection)
    if (selectedCards.length === 0) {
      setMode('combine');
      return;
    }

    if (selectedCards.length > 0 && selectedCards.length <= 4) {
      onDiscard(selectedCards);
      setSelectedCards([]);
      setMode('combine');
    }
  };

  const handleEndTurn = () => {
    onEndTurn();
    setSelectedCards([]);
    setMode('combine');
  };

  // Проверить, можно ли скомбинировать эту карту с выбранной
  const isCombinable = (cardId: string): boolean => {
    if (mode !== 'combine') return false;
    if (selectedCards.length !== 1) return false;
    if (selectedCards[0] === cardId) return false;

    const selectedCard = [...myState.hand, ...gameState.table].find(c => c.id === selectedCards[0]);
    const currentCard = [...myState.hand, ...gameState.table].find(c => c.id === cardId);

    if (!selectedCard || !currentCard) return false;

    // Проверить, есть ли рецепт
    if (findRecipe(selectedCard.element, currentCard.element) === null) {
      return false;
    }

    // Проверить ограничение СТОЛ + СТОЛ при 4 картах в руке
    const selectedFromTable = gameState.table.some(c => c.id === selectedCards[0]);
    const currentFromTable = gameState.table.some(c => c.id === cardId);

    if (selectedFromTable && currentFromTable && myState.hand.length >= 6) {
      return false; // Нельзя комбинировать стол+стол при 4 картах
    }

    return true;
  };

  // Проверить, нужно ли затемнить карту (невозможно скомбинировать)
  const isNotCombinable = (cardId: string): boolean => {
    if (mode !== 'combine') return false;
    if (selectedCards.length !== 1) return false;
    if (selectedCards[0] === cardId) return false;
    return !isCombinable(cardId);
  };

  // Получить имя выбранной карты
  const getSelectedCardName = (): string | null => {
    if (selectedCards.length !== 1) return null;
    const card = [...myState.hand, ...gameState.table].find(c => c.id === selectedCards[0]);
    return card ? `${card.icon} ${card.name}` : null;
  };

  return (
    <div className="game-board">
      {/* Spectator Banner */}
      {isSpectator && (
        <div className="spectator-banner">
          You are spectating this game
        </div>
      )}

      {/* Header */}
      <header className="game-header">
        <button
          className="surrender-btn"
          onClick={onSurrender}
          disabled={gameState.winner !== null}
        >
          Surrender
        </button>

        <div className="game-info">
          <h2 className="gold-gradient">🜛 Alchemy Duel</h2>
          <div className="game-stats">
            <span>Turn {gameState.turnNumber}</span>
            <span>•</span>
            <span className={isMyTurn ? 'turn-active' : ''}>
              {isMyTurn ? 'Your Turn' : 'Opponent\'s Turn'}
            </span>
            {isMyTurn && (
              <>
                <span>•</span>
                <span className={timeLeft <= 10 ? 'timer-warning' : 'timer'}>
                  {timeLeft}s
                </span>
              </>
            )}
          </div>
        </div>
        <button
          className="recipes-btn"
          onClick={() => setIsTreeModalOpen(true)}
          title="Press R to view Alchemy Tree"
        >
          📖 View Recipes
        </button>
      </header>

      {/* Opponent's hand (top) */}
      <div className="opponent-section">
        <div className="section-label">
          Player {opponentId} ({opponentState.hand.length} cards)
        </div>
        <div className="cards-container">
          {opponentState.hand.map((card) => (
            <Card
              key={card.id}
              card={card}
              size="small"
              disabled
              combining={combiningCards?.includes(card.id)}
            />
          ))}
        </div>
      </div>

      {/* Table (center) */}
      <div className="table-section">
        <div className="section-label">Table</div>
        <div className="cards-container">
          {gameState.table.map((card) => (
            <Card
              key={card.id}
              card={card}
              onClick={() => handleCardClick(card.id)}
              selected={selectedCards.includes(card.id)}
              combinable={isCombinable(card.id)}
              notCombinable={isNotCombinable(card.id)}
              disabled={!isMyTurn}
              animating={animatingCard === card.id}
              combining={combiningCards?.includes(card.id)}
            />
          ))}
        </div>
      </div>

      {/* Player's hand (bottom) */}
      <div className="player-section">
        <div className="section-label">
          Your Hand ({myState.hand.length}/6)
        </div>
        <div className="cards-container">
          {myState.hand.map((card) => (
            <Card
              key={card.id}
              card={card}
              onClick={() => handleCardClick(card.id)}
              selected={selectedCards.includes(card.id)}
              combinable={isCombinable(card.id)}
              notCombinable={isNotCombinable(card.id)}
              disabled={!isMyTurn}
              animating={animatingCard === card.id}
              combining={combiningCards?.includes(card.id)}
            />
          ))}
        </div>
      </div>

      {/* Control panel */}
      {isMyTurn && (
        <div className="control-panel">
          {/* Hints */}
          {mode === 'combine' && (
            <div className="recipe-hint">
              {selectedCards.length === 0 && <span>Select first card to combine</span>}
              {selectedCards.length === 1 && (
                <span>
                  Selected: <strong>{getSelectedCardName()}</strong> — Click a compatible card
                </span>
              )}
            </div>
          )}

          {mode === 'discard' && (
            <div className="recipe-hint">
              <span>Select 1-4 cards from hand or table to discard</span>
            </div>
          )}

          {/* Action buttons */}
          <div className="action-buttons-row">
            <button
              className={`action-btn ${mode === 'discard' ? 'active' : 'secondary'}`}
              onClick={() => {
                if (mode === 'discard') {
                  handleDiscard();
                } else {
                  setMode('discard');
                  setSelectedCards([]);
                }
              }}
              disabled={myState.discardedThisTurn}
            >
              {mode === 'discard' ? `Discard (${selectedCards.length})` : 'Discard Cards'}
            </button>

            <button className="action-btn primary" onClick={handleEndTurn}>
              End Turn
            </button>
          </div>

          {mode === 'discard' && (
            <button
              className="action-btn-small"
              onClick={() => { setMode('combine'); setSelectedCards([]); }}
            >
              Cancel
            </button>
          )}
        </div>
      )}

      {/* Victory overlay */}
      {gameState.winner && (
        <div className="victory-overlay">
          <div className="victory-message">
            <h1>🜛</h1>
            <h2>{gameState.winner === playerId ? 'You Won!' : 'You Lost!'}</h2>
            <p>Philosopher's Stone created!</p>
            <button className="action-btn primary" onClick={onBackToMenu}>
              Back to Menu
            </button>
          </div>
        </div>
      )}

      {/* Alchemy tree modal */}
      <AlchemyTreeModal
        isOpen={isTreeModalOpen}
        onClose={() => setIsTreeModalOpen(false)}
        selectedElement={
          selectedCards.length === 1
            ? [...myState.hand, ...gameState.table].find(c => c.id === selectedCards[0])?.element
            : null
        }
      />
    </div>
  );
};
